import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Card, message, Spin } from 'antd';
import { CameraOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Html5Qrcode } from 'html5-qrcode';
import VendorLayout from '../../components/VendorLayout';
import { getVendorBooth, Booth } from '../../services/vendorService';
import { auth } from '@festpix/services';
import { colors, shadows } from '@festpix/ui';

const { Title, Text } = Typography;

export default function VendorScanScreen() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [booth, setBooth] = useState<Booth | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    loadBoothInfo();
    return () => {
      stopScanner();
    };
  }, []);

  const loadBoothInfo = async () => {
    try {
      if (!user) return;
      const boothData = await getVendorBooth(user.uid);
      setBooth(boothData);
    } catch (error) {
      console.error('Error loading booth info:', error);
    }
  };

  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('API de camera nao disponivel. Use HTTPS ou localhost.');
        return false;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');

      if (videoDevices.length === 0) {
        message.error('Nenhuma camera encontrada no dispositivo.');
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  const requestCameraPermission = async (): Promise<boolean> => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('Camera requer conexao segura (HTTPS). Acesse via localhost ou HTTPS.');
        return false;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        message.error('Permissao de camera negada. Clique no icone de camera na barra de endereco para permitir.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        message.error('Nenhuma camera encontrada no dispositivo.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        message.error('Camera em uso por outro aplicativo. Feche outros apps que usam a camera.');
      } else if (err.name === 'OverconstrainedError') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          return true;
        } catch {
          message.error('Erro ao acessar camera: ' + err.message);
        }
      } else if (err.name === 'TypeError') {
        message.error('Camera requer conexao segura (HTTPS).');
      } else {
        message.error('Erro ao acessar camera: ' + err.name + ' - ' + err.message);
      }
      return false;
    }
  };

  const startScanner = async () => {

    try {
      setIsScanning(true);
      setScanned(false);

      // Check camera availability first
      const cameraAvailable = await checkCameraAvailability();
      if (!cameraAvailable) {
        setHasPermission(false);
        setIsScanning(false);
        return;
      }

      const permissionGranted = await requestCameraPermission();

      if (!permissionGranted) {
        setHasPermission(false);
        setIsScanning(false);
        return;
      }

      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          handleBarCodeScanned(decodedText);
        },
        () => {
          // QR code scan error (ignore, just means no QR found in frame)
        }
      );

      setHasPermission(true);
    } catch (err: any) {
      setHasPermission(false);
      setIsScanning(false);

      if (err.message?.includes('Permission')) {
        message.error('Permissao de camera negada. Por favor, permita o acesso a camera.');
      } else {
        message.error('Erro ao iniciar a camera: ' + err.message);
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore errors when stopping
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const handleBarCodeScanned = async (data: string) => {
    if (scanned) return;

    setScanned(true);
    await stopScanner();

    try {
      // Validate that it's a valid JSON with walletId
      const parsed = JSON.parse(data);
      if (!parsed.walletId) {
        throw new Error('QR Code invalido');
      }
      navigate('/vendor/charge', { state: { qrCodeData: data } });
    } catch {
      message.error('QR Code invalido. Por favor, escaneie um QR Code de carteira valido.');
      setScanned(false);
    }
  };

  const handleScanAgain = async () => {
    setScanned(false);
    await startScanner();
  };

  return (
    <VendorLayout boothName={booth?.name}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/vendor')}
          style={{ marginBottom: 16, padding: 0 }}
        >
          Voltar
        </Button>

        <Card
          style={{
            borderRadius: 16,
            boxShadow: shadows.lg,
            overflow: 'hidden',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>Escanear QR Code</Title>
            <Text style={{ color: colors.textSecondary }}>
              Aponte a camera para o QR Code do cliente
            </Text>
          </div>

          {/* Camera container */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              minHeight: 300,
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 16,
            }}
          >
            {!isScanning && !scanned && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 10,
                }}
              >
                <CameraOutlined style={{ fontSize: 48, color: '#fff', marginBottom: 16 }} />
                <Text style={{ color: '#fff', marginBottom: 24 }}>
                  Clique para iniciar a camera
                </Text>
                <Button
                  type="primary"
                  size="large"
                  icon={<CameraOutlined />}
                  onClick={startScanner}
                  style={{
                    background: colors.success,
                    borderColor: colors.success,
                    borderRadius: 8,
                  }}
                >
                  Iniciar Camera
                </Button>
              </div>
            )}

            {isScanning && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                <Spin size="large" />
              </div>
            )}

            <div
              id="qr-reader"
              style={{
                width: '100%',
                minHeight: 300,
              }}
            />
          </div>

          {hasPermission === false && (
            <div
              style={{
                textAlign: 'center',
                padding: 24,
                background: `${colors.error}10`,
                borderRadius: 12,
                marginBottom: 16,
              }}
            >
              <Text style={{ color: colors.error, display: 'block', marginBottom: 16 }}>
                Sem acesso a camera. Por favor, permita o acesso nas configuracoes do navegador.
              </Text>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={startScanner}
              >
                Tentar Novamente
              </Button>
            </div>
          )}

          {scanned && (
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<ReloadOutlined />}
                onClick={handleScanAgain}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 8,
                }}
              >
                Escanear Novamente
              </Button>
            </div>
          )}

          <Text
            style={{
              display: 'block',
              textAlign: 'center',
              color: colors.textSecondary,
              fontSize: 12,
              marginTop: 16,
            }}
          >
            Alinhe o QR Code dentro do quadro para escanear automaticamente
          </Text>
        </Card>
      </div>
    </VendorLayout>
  );
}
